import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/useAuth';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/api/client';
import type { Session, Project } from '@/api/client';
import {
  captureProjectListRollback,
  patchProjectListCaches,
} from '@/lib/projectListQueryCache';
import { rollbackProjectsAndReconcile } from '@/lib/projectMutationReconcile';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { KeyboardArrowUpIcon, KeyboardArrowDownIcon } from '@/components/ui/Icons';
import { ChevronRightIcon } from '@/components/ui/Icons';
import { autoFontSizePatchForDuration, sessionDurationMinutes } from '@/schedule/sessionShortDurationFontSize';
import { recordAmount } from '@/features/newdesign/projects/model/sessionRecord';
import {
  CORRECTIONS_PART_MAX_DIGITS,
  correctionsRecordedValue,
  initialCorrectionsChoice,
  type CorrectionsChoice,
} from './sessionConfirmCorrections';
import './SessionConfirmField.css';

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

function formatHM(hh: number, mm: number): string {
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

interface SessionConfirmFieldProps {
  session: Session;
  project: Project;
  /** Список сессий (например из ленивой загрузки в попапе); иначе берётся project.sessions */
  projectSessions?: Session[] | null;
  onSaved: (session: Session) => void;
  /** При false форма сбрасывается (закрытие карточки) */
  isExpanded?: boolean;
  /** Вызывается при нажатии «Далее» — передаём имя звукорежиссёра из auth для отображения в шапке */
  onEngineerFromAuth?: (displayName: string) => void;
  /** Черновик времени и студии для отображения в шапке (sessionId, draft). Callback должен быть стабильной ссылкой. */
  onDraftChange?: (sessionId: string, draft: { start?: string; end?: string; studio?: number }) => void;
}

const initStateFromSession = (s: Session) => {
  const [sh, sm] = (s.start || '10:00').slice(0, 5).split(':').map(Number);
  const [eh, em] = (s.end || '13:00').slice(0, 5).split(':').map(Number);
  const studioNum = parseInt(String(s.studio || '1').replace(/\D/g, ''), 10);
  return {
    startHours: sh ?? 10,
    startMinutes: sm ?? 0,
    endHours: eh ?? 13,
    endMinutes: em ?? 0,
    studio: studioNum >= 1 && studioNum <= 4 ? studioNum : 1,
    pages: s.stop_page != null ? String(s.stop_page) : '',
  };
};

function getEngineerDisplayName(user: { first_name?: string; last_name?: string; email?: string | null } | null): string {
  if (!user) return 'Не указан';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return name || (user.email ?? 'Не указан');
}

function StepArrow() {
  return (
    <span className="session-confirm__arrow" aria-hidden>
      <ChevronRightIcon size={40} />
    </span>
  );
}

export function SessionConfirmField({
  session,
  project,
  projectSessions,
  onSaved,
  isExpanded = true,
  onEngineerFromAuth,
  onDraftChange,
}: SessionConfirmFieldProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showError: showToastError, showSuccess: showToastSuccess } = useToast();
  const initial = initStateFromSession(session);
  const [step, setStep] = useState(1);
  const [startHours, setStartHours] = useState(initial.startHours);
  const [startMinutes, setStartMinutes] = useState(initial.startMinutes);
  const [endHours, setEndHours] = useState(initial.endHours);
  const [endMinutes, setEndMinutes] = useState(initial.endMinutes);
  const [studio, setStudio] = useState(initial.studio);
  const [pages, setPages] = useState(initial.pages);
  const isCorrections = session.session_type === 'правки';
  const correctionsMax = isCorrections
    ? (recordAmount(session, project, projectSessions ?? project.sessions ?? [])?.max ?? null)
    : null;
  // Предзаполнение берёт актуальную границу, но не должно сбрасывать набранное при каждом обновлении проекта
  const correctionsMaxRef = useRef(correctionsMax);
  correctionsMaxRef.current = correctionsMax;
  const [correctionsChoice, setCorrectionsChoice] = useState<CorrectionsChoice>(() =>
    initialCorrectionsChoice(isCorrections ? session.corrections_recorded : null, correctionsMax)
  );
  const [saving, setSaving] = useState(false);

  const isPodcastSession = session.session_type === 'запись' && session.session_subtype === 'podcast';
  const needsPagesStep = !isCorrections && !isPodcastSession;
  const studioStep = 3;
  const pagesStep = 4;
  const correctionsStep = 4;

  useEffect(() => {
    const next = initStateFromSession(session);
    setStartHours(next.startHours);
    setStartMinutes(next.startMinutes);
    setEndHours(next.endHours);
    setEndMinutes(next.endMinutes);
    setStudio(next.studio);
    setPages(next.pages);
    setCorrectionsChoice(initialCorrectionsChoice(isCorrections ? session.corrections_recorded : null, correctionsMaxRef.current));
  }, [session, isCorrections]);

  useEffect(() => {
    if (!isExpanded) {
      setStep(1);
      const next = initStateFromSession(session);
      setStartHours(next.startHours);
      setStartMinutes(next.startMinutes);
      setEndHours(next.endHours);
      setEndMinutes(next.endMinutes);
      setStudio(next.studio);
      setPages(next.pages);
      setCorrectionsChoice(initialCorrectionsChoice(isCorrections ? session.corrections_recorded : null, correctionsMaxRef.current));
    }
  }, [isExpanded, session, isCorrections]);

  useEffect(() => {
    if (!onDraftChange || !isExpanded) return;
    const start = step >= 2 ? formatHM(startHours, startMinutes) : undefined;
    const end = step >= 3 ? formatHM(endHours, endMinutes) : undefined;
    const studioVal = step >= studioStep ? studio : undefined;
    onDraftChange(session.id, { start, end, studio: studioVal });
  }, [onDraftChange, isExpanded, session.id, step, startHours, startMinutes, endHours, endMinutes, studio, studioStep]);

  const handleTimeChange = (type: string, direction: number) => {
    switch (type) {
      case 'startH':
        setStartHours((prev) => clamp(prev + direction, 0, 23));
        break;
      case 'startM': {
        let newVal = startMinutes + direction * 5;
        if (newVal > 59) {
          newVal = 0;
          setStartHours((prev) => Math.min(prev + 1, 23));
        }
        if (newVal < 0) {
          newVal = 55;
          setStartHours((prev) => Math.max(prev - 1, 0));
        }
        setStartMinutes(newVal);
        break;
      }
      case 'endH':
        setEndHours((prev) => clamp(prev + direction, 0, 23));
        break;
      case 'endM': {
        let newVal = endMinutes + direction * 5;
        if (newVal > 59) {
          newVal = 0;
          setEndHours((prev) => Math.min(prev + 1, 23));
        }
        if (newVal < 0) {
          newVal = 55;
          setEndHours((prev) => Math.max(prev - 1, 0));
        }
        setEndMinutes(newVal);
        break;
      }
      case 'studio':
        setStudio((prev) => clamp(prev + direction, 1, 4));
        break;
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      setStep(2);
      onEngineerFromAuth?.(getEngineerDisplayName(user));
    } else if (step === 2) {
      setStep(studioStep);
    } else if (step === studioStep) {
      if (needsPagesStep) {
        setStep(pagesStep);
      } else if (isCorrections) {
        setStep(correctionsStep);
      } else {
        void handleSave();
      }
    } else if (step === pagesStep) {
      void handleSave();
    }
  };

  const correctionsRecorded = isCorrections ? correctionsRecordedValue(correctionsChoice, correctionsMax) : null;

  const getLastSessionPage = (): number => {
    const readerId = session.readers?.[0]?.id ?? session.reader_id;
    if (!readerId) return 0;
    const fromReader = project.readers?.find((r) => r.id === readerId)?.pages_read;
    if (fromReader != null && fromReader > 0) return fromReader;
    const readerSessions = (projectSessions ?? project.sessions ?? [])
      .filter(
        (s) =>
          (s.readers?.some((r) => r.id === readerId) || s.reader_id === readerId) &&
          s.is_recorded &&
          !s.is_canceled &&
          s.stop_page != null
      )
      .map((s) => s.stop_page ?? 0);
    return readerSessions.length ? Math.max(...readerSessions) : 0;
  };

  const handleSave = async () => {
    if (!user) {
      showToastError('Войдите в систему');
      return;
    }

    const sTime = formatHM(startHours, startMinutes);
    const eTime = formatHM(endHours, endMinutes);
    const startTotal = startHours * 60 + startMinutes;
    const endTotal = endHours * 60 + endMinutes;
    const diffMins = endTotal > startTotal ? endTotal - startTotal : 0;

    if (diffMins <= 0) {
      showToastError('Время окончания должно быть больше времени начала');
      return;
    }

    let stopPage: number | null = null;
    if (!isCorrections && !isPodcastSession) {
      const pagesNum = pages.trim() ? parseInt(pages, 10) : 0;
      if (!pagesNum || pagesNum < 1) {
        showToastError('Введите корректное количество страниц');
        return;
      }
      // Последняя страница — чтеца (своё число или диапазон), иначе книги
      const pagesMax = recordAmount(session, project, projectSessions ?? project.sessions ?? [])?.max ?? null;
      if (pagesMax != null && pagesNum > pagesMax) {
        showToastError(`Последняя страница — ${pagesMax}`);
        return;
      }
      const lastPage = getLastSessionPage();
      if (pagesNum <= lastPage) {
        showToastError(`Страница должна быть больше ${lastPage}`);
        return;
      }
      stopPage = pagesNum;
    }

    if (correctionsRecorded === undefined) {
      showToastError('Укажите, сколько правок записано');
      return;
    }

    setSaving(true);
    const prevDur = sessionDurationMinutes(
      String(session.start).slice(0, 5),
      String(session.end).slice(0, 5)
    );
    const durationChanged = prevDur !== diffMins;
    const shortFont = durationChanged
      ? autoFontSizePatchForDuration(sTime, eTime, session.session_type)
      : null;
    const optimisticSession: Session = {
      ...session,
      start: sTime,
      end: eTime,
      studio: String(studio),
      is_recorded: true,
      stop_page: stopPage,
      ...(isCorrections ? { corrections_recorded: correctionsRecorded } : {}),
      soundengineer_id: user.id,
      soundengineer_name: getEngineerDisplayName(user),
      is_partial: false,
      ...(shortFont ?? {}),
    };
    const readerId = session.readers?.[0]?.id ?? session.reader_id;
    const patchProjectForOptimistic = (p: Project): Project => {
      if (p.id !== project.id) return p;
      const nextReaders =
        readerId && stopPage != null
          ? (p.readers ?? []).map((r) =>
              r.id === readerId
                ? { ...r, pages_read: Math.max(r.pages_read ?? 0, stopPage) }
                : r
            )
          : p.readers;
      return {
        ...p,
        readers: nextReaders,
        sessions: (p.sessions ?? []).map((s) =>
          s.id === session.id ? optimisticSession : s
        ),
      };
    };
    const rollback = captureProjectListRollback(queryClient);
    patchProjectListCaches(queryClient, (prev) =>
      prev ? prev.map(patchProjectForOptimistic) : prev
    );
    try {
      const updated = await api.sessions.update(session.id, {
        start: sTime,
        end: eTime,
        studio: String(studio),
        is_recorded: true,
        stop_page: stopPage,
        ...(isCorrections ? { corrections_recorded: correctionsRecorded } : {}),
        soundengineer_id: user.id,
        is_partial: false,
        ...(shortFont ?? {}),
      });
      showToastSuccess('Запись внесена');
      onSaved(updated);
    } catch (err) {
      void rollbackProjectsAndReconcile(queryClient, rollback).catch(() => {});
      const msg = (err as Error).message;
      const isSoundengineerFk = /sessions_soundengineer_id_fkey|violates foreign key constraint.*soundengineer/i.test(msg);
      showToastError(isSoundengineerFk ? 'Только звукорежиссёры могут вносить сессии через этот интерфейс' : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleFillAllPages = () => {
    if (project.pages) {
      setPages(String(project.pages));
    }
  };

  const TimeField = ({
    value,
    label,
    type,
    isStudio = false,
  }: {
    value: string | number;
    label: string;
    type: string;
    isStudio?: boolean;
  }) => (
    <div className={`session-confirm__time-field ${isStudio ? 'session-confirm__time-field_type_studio' : ''}`}>
      <span className="session-confirm__time-value">{value} {label}</span>
      <div
        className="session-confirm__time-zone session-confirm__time-zone_top"
        onClick={() => handleTimeChange(type, 1)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleTimeChange(type, 1)}
      >
        <KeyboardArrowUpIcon size={16} className="session-confirm__time-zone-arrow" />
      </div>
      <div
        className="session-confirm__time-zone session-confirm__time-zone_bottom"
        onClick={() => handleTimeChange(type, -1)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleTimeChange(type, -1)}
      >
        <KeyboardArrowDownIcon size={16} className="session-confirm__time-zone-arrow" />
      </div>
    </div>
  );

  const stepLabel = (() => {
    if (step === 1) return 'Укажите время начала записи';
    if (step === 2) return 'Укажите время окончания записи';
    if (step === studioStep) return 'Выберите студию';
    if (step === pagesStep && needsPagesStep) return 'Введите страницу';
    if (step === correctionsStep && isCorrections) return 'Правки';
    return '';
  })();

  return (
    <div className="session-confirm">
      {/* Step 1: Время начала */}
      {step === 1 && (
        <div className="session-confirm__step">
          <span className="session-confirm__label">{stepLabel}</span>
          <StepArrow />
          <div className="session-confirm__time-row">
            <TimeField label="ч." type="startH" value={String(startHours).padStart(2, '0')} />
            <span className="session-confirm__time-sep">:</span>
            <TimeField label="м." type="startM" value={String(startMinutes).padStart(2, '0')} />
          </div>
          <StepArrow />
          <div className="session-confirm__btn-wrap">
            <Button variant="primary" size="m" type="button" onClick={handleNextStep}>Далее</Button>
          </div>
        </div>
      )}

      {/* Step 2: Время окончания */}
      {step === 2 && (
        <div className="session-confirm__step">
          <span className="session-confirm__label">{stepLabel}</span>
          <StepArrow />
          <div className="session-confirm__time-row">
            <TimeField label="ч." type="endH" value={String(endHours).padStart(2, '0')} />
            <span className="session-confirm__time-sep">:</span>
            <TimeField label="м." type="endM" value={String(endMinutes).padStart(2, '0')} />
          </div>
          <StepArrow />
          <div className="session-confirm__btn-wrap">
            <Button variant="primary" size="m" type="button" onClick={handleNextStep}>Далее</Button>
          </div>
        </div>
      )}

      {/* Студия */}
      {step === studioStep && (
        <div className="session-confirm__step">
          <span className="session-confirm__label">{stepLabel}</span>
          <StepArrow />
          <div className="session-confirm__time-row">
            <TimeField label="студия" type="studio" value={studio} isStudio />
          </div>
          <StepArrow />
          <div className="session-confirm__btn-wrap">
            {needsPagesStep || isCorrections ? (
              <Button variant="primary" size="m" type="button" onClick={handleNextStep}>Далее</Button>
            ) : (
              <Button variant="primary" size="m" type="button" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Сохранение…' : 'Внести'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Страницы */}
      {step === pagesStep && needsPagesStep && (
        <div className="session-confirm__step">
          <span className="session-confirm__label">{stepLabel}</span>
          <StepArrow />
          <div className="session-confirm__pages-wrap">
            <Input
              type="text"
              inputMode="numeric"
              value={pages}
              onChange={(e) => setPages(e.target.value.replace(/\D/g, ''))}
              placeholder="Страницы"
              inputClassName="session-confirm__pages-input"
            />
            <button
              type="button"
              className="session-confirm__fill-btn"
              onClick={handleFillAllPages}
              title="Добавить максимальное количество страниц"
            >
              ВСЕ
            </button>
          </div>
          <StepArrow />
          <div className="session-confirm__btn-wrap">
            <Button variant="primary" size="m" type="button" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Сохранение…' : 'Внести'}
            </Button>
          </div>
        </div>
      )}

      {/* Правки */}
      {step === correctionsStep && isCorrections && (
        <div className="session-confirm__step">
          <span className="session-confirm__label">{stepLabel}</span>
          <StepArrow />
          <div className="session-confirm__corrections">
            <div className="session-confirm__choice" role="group" aria-label="Сколько правок записано">
              <button
                type="button"
                className="session-confirm__choice-btn"
                aria-pressed={correctionsChoice.mode === 'all'}
                onClick={() => setCorrectionsChoice({ mode: 'all' })}
              >
                Все записаны
              </button>
              <button
                type="button"
                className="session-confirm__choice-btn"
                aria-pressed={correctionsChoice.mode === 'part'}
                onClick={() => setCorrectionsChoice((prev) => (prev.mode === 'part' ? prev : { mode: 'part', text: '' }))}
              >
                Часть
              </button>
            </div>
            {correctionsChoice.mode === 'part' && (
              <div className="session-confirm__corrections-count">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={CORRECTIONS_PART_MAX_DIGITS}
                  value={correctionsChoice.text}
                  onChange={(e) => setCorrectionsChoice({ mode: 'part', text: e.target.value.replace(/\D/g, '') })}
                  placeholder="Правок"
                  aria-label="Сколько правок записано"
                  aria-invalid={correctionsRecorded === undefined && correctionsChoice.text !== ''}
                  inputClassName="session-confirm__pages-input"
                />
                {correctionsMax != null && <span className="session-confirm__corrections-of">из {correctionsMax}</span>}
              </div>
            )}
          </div>
          <StepArrow />
          <div className="session-confirm__btn-wrap">
            <Button
              variant="primary"
              size="m"
              type="button"
              onClick={() => void handleSave()}
              disabled={saving || correctionsRecorded === undefined}
            >
              {saving ? 'Сохранение…' : 'Внести'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
